import React, { useMemo, useRef } from 'react';
import { Box, Typography, Tooltip, Button } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import IconButton from '@mui/material/IconButton';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';

import { Category } from '../../types/Category';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import FormGroup from '@mui/material/FormGroup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Collapse from '@mui/material/Collapse';
import { useHelpContent } from '../../utils/HelpUtils';

const highlightColors = [
  '#ffe066', // yellow
  '#ffadad', // red
  '#b5ead7', // green
  '#a0c4ff', // blue
  '#ffd6a5', // orange
  '#caffbf', // light green
  '#9bf6ff', // cyan
  '#bdb2ff', // purple
];

function highlightText(
  text: string,
  searchWords: string[],
  caseSensitive: boolean,
  regex: RegExp,
  colorMap: { [key: string]: string },
  selectedMatch?: { matchIdx: number; wordIdx: number },
  wholeWordOnly?: boolean
) {
  if (!searchWords.length || !text) return text;
  const normalizedText = text.replace(/\n/g, ' ');
  let lastIndex = 0;
  let matchIdx = 0;
  const parts = [];
  
  if (wholeWordOnly) {
    // Custom whole word highlighting
    searchWords.forEach((searchWord, wordIdx) => {
      const searchRegex = new RegExp(searchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseSensitive ? 'g' : 'gi');
      let match;
      
      while ((match = searchRegex.exec(normalizedText)) !== null) {
        const matchStart = match.index;
        const matchEnd = matchStart + match[0].length;
        
        // Check if it's a whole word by examining characters before and after
        const beforeChar = matchStart > 0 ? normalizedText[matchStart - 1] : '';
        const afterChar = matchEnd < normalizedText.length ? normalizedText[matchEnd] : '';
        
        // Check if characters before and after are not word characters
        const isWordChar = (char: string) => /[a-zA-Z0-9_\u0590-\u05FF]/.test(char);
        
        if (!isWordChar(beforeChar) && !isWordChar(afterChar)) {
          if (matchStart > lastIndex) {
            parts.push(text.slice(lastIndex, matchStart));
          }
          
          const colorKey = caseSensitive ? searchWord : searchWord.toLowerCase();
          const color = colorMap[colorKey] || '#ffe066';
          const isSelected = selectedMatch && selectedMatch.matchIdx === matchIdx && selectedMatch.wordIdx === wordIdx;
          
          parts.push(
            <span
              key={`${matchStart}-${wordIdx}`}
              style={{
                backgroundColor: color,
                border: isSelected ? '2px solid #ff9800' : undefined,
                borderRadius: isSelected ? 3 : undefined,
                boxShadow: isSelected ? '0 0 0 2px #ffe066' : undefined,
                padding: isSelected ? '0 2px' : undefined,
                position: 'relative',
                zIndex: isSelected ? 1 : 0,
              }}
            >
              {match[0]}
            </span>
          );
          
          lastIndex = matchEnd;
          matchIdx++;
        }
      }
    });
  } else {
    // Regular regex highlighting - need to check which word matched
    let match;
    while ((match = regex.exec(normalizedText)) !== null) {
      if (!match[0]) {
        regex.lastIndex++;
        continue;
      }

      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      const matchedText = match[0];
      
      // Find which word matched by checking against each search word
      let wordIdx = -1;
      for (let i = 0; i < searchWords.length; i++) {
        const searchWord = searchWords[i];
        const searchRegex = new RegExp(searchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseSensitive ? 'g' : 'gi');
        if (searchRegex.test(matchedText)) {
          wordIdx = i;
          break;
        }
      }
      
      const colorKey = caseSensitive ? searchWords[wordIdx] : searchWords[wordIdx]?.toLowerCase();
      const color = colorMap[colorKey] || '#ffe066';
      const isSelected = selectedMatch && selectedMatch.matchIdx === matchIdx && selectedMatch.wordIdx === wordIdx;
      parts.push(
        <span
          key={match.index}
          style={{
            backgroundColor: color,
            border: isSelected ? '2px solid #ff9800' : undefined,
            borderRadius: isSelected ? 3 : undefined,
            boxShadow: isSelected ? '0 0 0 2px #ffe066' : undefined,
            padding: isSelected ? '0 2px' : undefined,
            position: 'relative',
            zIndex: isSelected ? 1 : 0,
          }}
        >
          {matchedText}
        </span>
      );
      lastIndex = match.index + matchedText.length;
      matchIdx++;
    }
  }
  
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

function parseSearchTerms(input: string): [string[], boolean] {
  if (!input.trim()) return [[], false];

  // Check for quoted phrases first
  const quotedPhrases: string[] = [];
  let remainingInput = input;
  
  // Extract quoted phrases (both English and Hebrew quotes)
  const quoteRegex = /["״]([^"״]+)["״]/g;
  let match;
  while ((match = quoteRegex.exec(input)) !== null) {
    quotedPhrases.push(match[1]);
    // Remove the quoted phrase from remaining input
    remainingInput = remainingInput.replace(match[0], '');
  }
  
  // Split remaining input by whitespace and filter out empty terms
  const remainingTerms = remainingInput.split(/\s+/).filter(term => term.length > 0);
  
  // Combine quoted phrases and remaining terms
  const allTerms = [...quotedPhrases, ...remainingTerms];
  
  const hasRegex = allTerms.some(term => term.includes('*') || term.includes('?') || term.includes('+') || term.includes('[') || term.includes(']') || term.includes('(') || term.includes(')') || term.includes('\\') || term.includes('^') || term.includes('$') || term.includes('|') || term.includes('{') || term.includes('}') || term.includes('.'));
  
  if (hasRegex) {
    try {
      const regex = new RegExp(input, 'g');
      return [[input], true];
    } catch {
      return [allTerms, false];
    }
  }
  
  return [allTerms, false];
}

interface SubTreeViewProps {
  rootCategory: Category;
  allCategories: Category[];
  subcases: string[];
  includeDescriptions: boolean;
  level?: number;
  sourceTexts?: { [key: string]: string[] }; // Map of content ID to array of source texts
}

function flattenTree(
  category: Category,
  level: number,
  includeDescriptions: boolean,
  subcases: string[],
  searchWords: string[],
  caseSensitive: boolean,
  regex: RegExp,
  colorMap: { [key: string]: string },
  searchFields: { category: boolean; description: boolean; subcase: boolean; content: boolean }
): Array<{
  type: 'category' | 'contentCategory' | 'description' | 'content' | 'subcase';
  content: string;
  level: number;
  id: string;
}> {
  const items: Array<{
    type: 'category' | 'contentCategory' | 'description' | 'content' | 'subcase';
    content: string;
    level: number;
    id: string;
  }> = [];

  // Add category name if it should be searched
  if (searchFields.category && category.name && category.name.trim()) {
    items.push({
      type: category.contentCategory ? 'contentCategory' : 'category',
      content: category.name,
      level,
      id: `category-${category.id}`
    });
  }

  // Add description if it should be searched and exists
  if (includeDescriptions && searchFields.description && category.description && category.description.trim()) {
    items.push({
      type: 'description',
      content: category.description,
      level: level + 1,
      id: `description-${category.id}`
    });
  }

  // Add contents if they match subcases
  if (subcases.length > 0 && category.contents) {
    category.contents
      .filter(content => subcases.includes(content.subcase))
      .forEach(content => {
        if (content.subcase && searchFields.subcase && content.subcase.trim()) {
          items.push({
            type: 'subcase',
            content: content.subcase,
            level: level + 1,
            id: `subcase-${content.id}`
          });
        }
        if (content.text && searchFields.content && content.text.trim()) {
          items.push({
            type: 'content',
            content: content.text,
            level: level + 1,
            id: `content-${content.id}`
          });
        }
      });
  }

  // Recursively add children
  if (category.children) {
    category.children.forEach(child => {
      items.push(...flattenTree(
        child,
        level + 1,
        includeDescriptions,
        subcases,
        searchWords,
        caseSensitive,
        regex,
        colorMap,
        searchFields
      ));
    });
  }

  return items;
}

const MAX_HISTORY = 5;

const SubTreeView: React.FC<SubTreeViewProps> = ({
  rootCategory,
  allCategories,
  subcases,
  includeDescriptions,
  level = 0,
  sourceTexts
}) => {
  const [currentMatch, setCurrentMatch] = React.useState(0);
  const [itemHeights, setItemHeights] = React.useState<{ [key: string]: number }>({});
  const contentRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({});
  const virtuosoRef = React.useRef<VirtuosoHandle>(null);

  const [search, setSearch] = React.useState('');
  const [caseSensitive, setCaseSensitive] = React.useState(false);
  const [wholeWordOnly, setWholeWordOnly] = React.useState(false);
  const [searchHistory, setSearchHistory] = React.useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('subtree_search_history');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [searchBoxAnchor, setSearchBoxAnchor] = React.useState<null | HTMLElement>(null);

  // Search fields state
  const [searchFields, setSearchFields] = React.useState({
    category: true,
    description: true,
    subcase: true,
    content: true,
  });

  // State for expanded source texts
  const [expandedSources, setExpandedSources] = React.useState<{ [key: string]: boolean }>({});
  const [showSourceTexts, setShowSourceTexts] = React.useState(false);

  const { getTooltipTextForState } = useHelpContent();

  // Save history to localStorage
  React.useEffect(() => {
    localStorage.setItem('subtree_search_history', JSON.stringify(searchHistory));
  }, [searchHistory]);

  // Print functionality
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = generatePrintContent();
    const isRTL = /[\u0590-\u05FF]/.test(rootCategory.name);
    const rtlDir = isRTL ? 'dir="rtl"' : '';
    const rtlBodyStyle = isRTL ? 'direction: rtl; text-align: right;' : '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html ${rtlDir}>
        <head>
          <title>Subtree View - ${rootCategory.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; ${rtlBodyStyle} }
            .category { margin: 10px 0; }
            .content { margin: 5px 0; }
            .description { margin: 5px 0; font-style: italic; color: #666; }
            .level-0 { font-weight: bold; font-size: 18px; }
            .level-1 { font-weight: bold; font-size: 16px; }
            .level-2 { font-weight: bold; font-size: 14px; }
            .level-3 { font-weight: bold; font-size: 12px; }
            .level-4 { font-weight: bold; font-size: 12px; }
            .level-5 { font-weight: bold; font-size: 12px; }
            .content-category { color: #87CEEB; }
            .regular-category { color: #1976d2; }
            .content-text { margin: 5px 0; }
            .search-highlight { background-color: #ffe066; }
            @media print { body { margin: 10px; } }
          </style>
        </head>
        <body>
          <h1>Subtree View: ${rootCategory.name}</h1>
          <p><strong>Generated on:</strong> ${new Date().toLocaleString()}</p>
          ${search ? `<p><strong>Search:</strong> "${search}"</p>` : ''}
          <div class="tree-content">${printContent}</div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleExportDocx = async () => {
    const printItems = flattenTree(
      rootCategory,
      0,
      includeDescriptions,
      subcases,
      searchWords,
      caseSensitive,
      regex,
      colorMap,
      searchFields
    );

    const isRTL = /[\u0590-\u05FF]/.test(rootCategory.name);
    
    const children = [
      new Paragraph({
        text: `Subtree View: ${rootCategory.name}`,
        heading: HeadingLevel.HEADING_1,
        alignment: isRTL ? AlignmentType.RIGHT : AlignmentType.LEFT,
      }),
      new Paragraph({
        text: `Generated on: ${new Date().toLocaleString()}`,
        alignment: isRTL ? AlignmentType.RIGHT : AlignmentType.LEFT,
      }),
    ];

    if (search) {
      children.push(
        new Paragraph({
          text: `Search: "${search}"`,
          alignment: isRTL ? AlignmentType.RIGHT : AlignmentType.LEFT,
        })
      );
    }

    children.push(new Paragraph({ text: '' })); // Empty line

    printItems
      .filter(item => item.content && item.content.trim() !== '')
      .forEach(item => {
        let text = '';
        let icon = '';

        switch (item.type) {
          case 'category':
          case 'contentCategory':
            icon = item.type === 'contentCategory' ? '📄' : '📁';
            text = isRTL ? `${item.content} ${icon}` : `${icon} ${item.content}`;
            break;
          case 'subcase':
            icon = '📝';
            text = isRTL ? `${item.content} ${icon}` : `${icon} ${item.content}`;
            break;
          case 'content':
            text = item.content;
            break;
          case 'description':
            icon = '💬';
            text = isRTL ? `${item.content} ${icon}` : `${icon} ${item.content}`;
            break;
        }

        if (text) {
          // Calculate indentation based on level
          const indent = item.level * 720; // 720 twips = 0.5 inches
          
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: text,
                  size: item.level === 0 ? 32 : item.level === 1 ? 28 : 24,
                  bold: item.type === 'category' || item.type === 'contentCategory',
                  italics: item.type === 'description',
                }),
              ],
              alignment: isRTL ? AlignmentType.RIGHT : AlignmentType.LEFT,
              indent: {
                left: isRTL ? 0 : indent,
                right: isRTL ? indent : 0,
              },
            })
          );
        }
      });

    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 1440,
                right: 1440,
                bottom: 1440,
                left: 1440,
              },
            },
          },
          children: children,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // Create a safe filename that preserves Hebrew and other Unicode characters
    const safeName = rootCategory.name
      .replace(/[<>:"/\\|?*]/g, '-') // Remove Windows filename invalid characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    link.download = `subtree-view-${safeName}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const generatePrintContent = () => {
    const printItems = flattenTree(
      rootCategory,
      0,
      includeDescriptions,
      subcases,
      searchWords,
      caseSensitive,
      regex,
      colorMap,
      searchFields
    );

    const isRTL = /[\u0590-\u05FF]/.test(rootCategory.name);
    const rtlStyle = isRTL ? 'direction: rtl; text-align: right;' : '';
    const rtlPadding = isRTL ? 'padding-right' : 'padding-left';

    return printItems
      .filter(item => item.content && item.content.trim() !== '')
      .map(item => {
        const levelClass = `level-${Math.min(item.level, 5)}`;
        const paddingValue = item.level * 20;
        
        switch (item.type) {
          case 'category':
          case 'contentCategory': {
            const categoryClass = item.type === 'contentCategory' ? 'content-category' : 'regular-category';
            const icon = item.type === 'contentCategory' ? '📄' : '📁';
            return `<div class="category ${levelClass} ${categoryClass}" style="${rtlStyle} ${rtlPadding}: ${paddingValue}px;">${icon} ${item.content}</div>`;
          }
                  case 'subcase': {
          return `<div class="subcase-text" style="${rtlStyle} ${rtlPadding}: ${paddingValue}px;">📝 ${item.content}</div>`;
        }
        case 'content': {
          return `<div class="content-text" style="${rtlStyle} ${rtlPadding}: ${paddingValue}px;">${item.content}</div>`;
        }
          case 'description': {
            return `<div class="description" style="${rtlStyle} ${rtlPadding}: ${paddingValue}px;">💬 ${item.content}</div>`;
          }
          default:
            return '';
        }
      })
      .filter(item => item.trim() !== '')
      .join('');
  };

  // Add to history when search is submitted (on Enter or blur)
  const addToHistory = (value: string) => {
    if (!value.trim()) return;
    setSearchHistory(prev => {
      const filtered = prev.filter(item => item !== value);
      return [value, ...filtered].slice(0, MAX_HISTORY);
    });
  };

  // Handle search box events
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };
  const handleSearchKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      addToHistory(search);
    }
  };
  const handleSearchBlur = () => {
    addToHistory(search);
  };
  const handleSearchFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setSearchBoxAnchor(e.currentTarget);
  };
  const handleHistoryClick = (item: string) => {
    setSearch(item);
    setSearchBoxAnchor(null);
  };

  // Parse search terms and create regex
  const [searchWords, isRegex] = useMemo(() => parseSearchTerms(search), [search]);
  const regex = useMemo(() => {
    if (isRegex) {
      try {
        return new RegExp(searchWords[0], caseSensitive ? 'g' : 'gi');
      } catch {
        return new RegExp('', 'g');
      }
    }
    
    if (!searchWords.length) return new RegExp('', 'g');
    
    if (wholeWordOnly) {
      // For whole word search, we'll use a custom approach
              const patterns = searchWords.map(word => {
          const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // Use word boundaries that work with Unicode
          const pattern = `\\b${escaped}\\b`;
          return pattern;
        });
        
        const pattern = patterns.join('|');
      return new RegExp(pattern, caseSensitive ? 'g' : 'gi');
    } else {
      // Regular search without whole word
      const patterns = searchWords.map(word => {
        const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return escaped;
      });
      
      const pattern = patterns.join('|');
      return new RegExp(pattern, caseSensitive ? 'g' : 'gi');
    }
  }, [searchWords, isRegex, caseSensitive, wholeWordOnly]);

  // Create color map for highlighting
  const colorMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    searchWords.forEach((word, index) => {
      const key = caseSensitive ? word : word.toLowerCase();
      map[key] = highlightColors[index % highlightColors.length];
    });
    return map;
  }, [searchWords, caseSensitive]);

  // Flatten tree for display
  const items = useMemo(() => {
    return flattenTree(
      rootCategory,
      0,
      includeDescriptions,
      subcases,
      searchWords,
      caseSensitive,
      regex,
      colorMap,
      searchFields
    );
  }, [rootCategory, includeDescriptions, subcases, searchWords, caseSensitive, regex, colorMap, searchFields]);

  // Find all matches for navigation
  const allMatches = useMemo(() => {
    const matches: Array<{ itemIdx: number; matchIdx: number; wordIdx: number }> = [];
    
    items.forEach((item, itemIdx) => {
      if (!item.content) return;
      
      const normalizedText = item.content.replace(/\n/g, ' ');
      
      if (wholeWordOnly) {
        // Custom whole word search for Unicode support
        searchWords.forEach((searchWord, wordIdx) => {
          const searchRegex = new RegExp(searchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseSensitive ? 'g' : 'gi');
          let match;
          let matchIdx = 0;
          
          while ((match = searchRegex.exec(normalizedText)) !== null) {
            const matchStart = match.index;
            const matchEnd = matchStart + match[0].length;
            
            // Check if it's a whole word by examining characters before and after
            const beforeChar = matchStart > 0 ? normalizedText[matchStart - 1] : '';
            const afterChar = matchEnd < normalizedText.length ? normalizedText[matchEnd] : '';
            
            // Check if characters before and after are not word characters
            const isWordChar = (char: string) => /[a-zA-Z0-9_\u0590-\u05FF]/.test(char);
            
            if (!isWordChar(beforeChar) && !isWordChar(afterChar)) {
              matches.push({ itemIdx, matchIdx, wordIdx });
              matchIdx++;
            }
          }
        });
      } else {
        // Regular regex search
        let match;
        let matchIdx = 0;
        
        while ((match = regex.exec(normalizedText)) !== null) {
          if (!match[0]) {
            regex.lastIndex++;
            continue;
          }
          
          let wordIdx = -1;
          for (let i = 1; i < match.length; i++) {
            if (match[i] !== undefined) {
              wordIdx = i - 1;
              break;
            }
          }
          
          matches.push({ itemIdx, matchIdx, wordIdx });
          matchIdx++;
        }
      }
    });
    
    return matches;
  }, [items, regex, wholeWordOnly, searchWords, caseSensitive]);

  function getSelectedMatchForItem(index: number) {
    const match = allMatches.find(m => m.itemIdx === index);
    return match ? { matchIdx: match.matchIdx, wordIdx: match.wordIdx } : undefined;
  }

  const getItemHeight = (index: number) => {
    const item = items[index];
    if (!item) return 50;
    
    const baseHeight = 40;
    const contentHeight = Math.ceil(item.content.length / 80) * 20;
    return Math.max(baseHeight, contentHeight);
  };

  // Render function for each item
  const renderItem = (item: any, index: number) => {
    if (!item.content) return null;
    const paddingStyle = isRTL
      ? { paddingRight: item.level * 4 }
      : { paddingLeft: item.level * 4 };
    const selectedMatch = getSelectedMatchForItem(index);
    switch (item.type) {
      case 'category':
      case 'contentCategory': {
        const highlighted = highlightText(item.content, searchWords, caseSensitive, regex, colorMap, selectedMatch, wholeWordOnly);
        // Find the category in allCategories to check if it's a content category
        const isContentCategory = item.type === 'contentCategory';

        // Use DescriptionIcon for content categories, FolderIcon for regular categories
        const CategoryIcon = isContentCategory ? DescriptionIcon : FolderIcon;
        const iconColor = isContentCategory ? '#87CEEB' : '#1976d2';

        return (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 1,
              px: 2,
              borderBottom: '1px solid #f0f0f0',
              minHeight: getItemHeight(index),
              ...paddingStyle,
            }}
          >
            <CategoryIcon sx={{ color: iconColor, mr: 1, fontSize: 20 }} />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                wordBreak: 'break-word',
                flex: 1,
                color: iconColor,
              }}
            >
              {highlighted}
            </Typography>
          </Box>
        );
      }
      case 'subcase': {
        const highlighted = highlightText(item.content, searchWords, caseSensitive, regex, colorMap, selectedMatch, wholeWordOnly);
        const iconMargin = isRTL ? { ml: 1 } : { mr: 1 };
        return (
          <Box
            key={item.id}
            sx={{
              py: 1,
              px: 2,
              borderBottom: '1px solid #f0f0f0',
              minHeight: getItemHeight(index),
              ...paddingStyle,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', direction: isRTL ? 'rtl' : 'ltr' }}>
              <StickyNote2OutlinedIcon sx={{ color: '#ff9800', fontSize: 16, flexShrink: 0, ...iconMargin }} />
              <Typography
                variant="body2"
                sx={{
                  wordBreak: 'break-word',
                  flex: 1,
                  color: '#333',
                  lineHeight: 1.4,
                }}
              >
                {highlighted}
              </Typography>
            </Box>
          </Box>
        );
      }
      case 'content': {
        const highlighted = highlightText(item.content, searchWords, caseSensitive, regex, colorMap, selectedMatch, wholeWordOnly);
        return (
          <Box
            key={item.id}
            sx={{
              py: 1,
              px: 2,
              borderBottom: '1px solid #f0f0f0',
              minHeight: getItemHeight(index),
              ...paddingStyle,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-word',
                flex: 1,
                color: '#333',
                lineHeight: 1.4,
              }}
            >
              {highlighted}
            </Typography>
          </Box>
        );
      }
      case 'description': {
        const highlighted = highlightText(item.content, searchWords, caseSensitive, regex, colorMap, selectedMatch, wholeWordOnly);
        return (
          <Box
            key={item.id}
            sx={{
              py: 1,
              px: 2,
              borderBottom: '1px solid #f0f0f0',
              minHeight: getItemHeight(index),
              ...paddingStyle,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontStyle: 'italic',
                color: '#666',
                wordBreak: 'break-word',
                lineHeight: 1.4,
              }}
            >
              {highlighted}
            </Typography>
          </Box>
        );
      }
      default:
        return null;
    }
  };

  // Search match navigation UI
  React.useEffect(() => {
    if (allMatches.length && virtuosoRef.current) {
      const idx = allMatches[currentMatch]?.itemIdx;
      console.log('Scrolling to index:', idx);
      setTimeout(() => {
        virtuosoRef.current?.scrollToIndex({
          index: idx,
          align: 'start',
          behavior: 'auto',
        });
      }, 50);
    }
  }, [currentMatch, allMatches]);

  const isRTL = /[\u0590-\u05FF]/.test(rootCategory.name);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Sub Tree View
          </Typography>
        </Box>
      </Box>
      <FormGroup row sx={{ gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={getTooltipTextForState('subtree-search-category', true)}>
            <FormControlLabel
              control={<Checkbox checked={searchFields.category} onChange={e => setSearchFields(prev => ({ ...prev, category: e.target.checked }))} />}
              label="Category names"
            />
          </Tooltip>

          <Tooltip title={getTooltipTextForState('subtree-search-description', true)}>
            <FormControlLabel
              control={<Checkbox checked={searchFields.description} onChange={e => setSearchFields(prev => ({ ...prev, description: e.target.checked }))} />}
              label="Descriptions"
            />
          </Tooltip>
          <Tooltip title={getTooltipTextForState('subtree-search-subcase', true)}>
            <FormControlLabel
              control={<Checkbox checked={searchFields.subcase} onChange={e => setSearchFields(prev => ({ ...prev, subcase: e.target.checked }))} />}
              label="Subcases"
            />
          </Tooltip>
          <Tooltip title={getTooltipTextForState('subtree-search-content', true)}>
            <FormControlLabel
              control={<Checkbox checked={searchFields.content} onChange={e => setSearchFields(prev => ({ ...prev, content: e.target.checked }))} />}
              label="Content text"
            />
          </Tooltip>
          {sourceTexts && (
            <Tooltip title={getTooltipTextForState('subtree-show-sources', true)}>
              <FormControlLabel
                control={<Checkbox checked={showSourceTexts} onChange={e => setShowSourceTexts(e.target.checked)} />}
                label="Show source texts"
              />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            size="small"
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportDocx}
            size="small"
          >
            Export DOCX
          </Button>
        </Box>
      </FormGroup>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Tooltip title={getTooltipTextForState('subtree-search-input', true)}>
          <TextField
            value={search}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchBlur}
            onFocus={handleSearchFocus}
            placeholder='Search (multiple words or "phrases")'
            size='small'
            sx={{ minWidth: 320 }}
            InputProps={{
              endAdornment: (
                search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearch('')}
                      aria-label="Clear search"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              )
            }}
          />
        </Tooltip>
        <Popper open={!!searchBoxAnchor && !search && searchHistory.length > 0} anchorEl={searchBoxAnchor} placement="bottom-start" style={{ zIndex: 1301 }}>
          <Paper sx={{ width: 320 }}>
            <List dense>
              {searchHistory.map((item, idx) => (
                <ListItemButton key={item + idx} onMouseDown={() => handleHistoryClick(item)}>
                  <ListItemText primary={item} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
        <FormGroup row sx={{ gap: 1 }}>
          <Tooltip title={getTooltipTextForState('subtree-case-sensitive', true)}>
            <FormControlLabel
              control={<Checkbox checked={caseSensitive} onChange={e => setCaseSensitive(e.target.checked)} />}
              label="Case sensitive"
            />
          </Tooltip>
          <Tooltip title={getTooltipTextForState('subtree-whole-word', true)}>
            <FormControlLabel
              control={<Checkbox checked={wholeWordOnly} onChange={e => setWholeWordOnly(e.target.checked)} />}
              label="Whole word only"
            />
          </Tooltip>
        </FormGroup>
      </Box>
      {search && allMatches.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 600 }}>
            {currentMatch + 1} / {allMatches.length}
          </Typography>
          <Tooltip title={getTooltipTextForState('subtree-previous-match', true)}>
            <IconButton
              size="small"
              onClick={() => setCurrentMatch((prev) => (prev - 1 + allMatches.length) % allMatches.length)}
              disabled={allMatches.length === 0}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={getTooltipTextForState('subtree-next-match', true)}>
            <IconButton
              size="small"
              onClick={() => setCurrentMatch((prev) => (prev + 1) % allMatches.length)}
              disabled={allMatches.length === 0}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <Virtuoso
        ref={virtuosoRef}
        style={{ height: 800, width: '100%' }}
        totalCount={items.length}
        itemContent={(index: number) => renderItem(items[index], index)}
        computeItemKey={(index) => items[index].id}
        components={{
          Scroller: React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>((props, ref) => (
            <div {...props} ref={ref} dir={isRTL ? 'rtl' : 'ltr'} />
          ))
        }}
      />
    </>
  );
};

export default SubTreeView;